// Kopf und Anlegen der Pflegeseiten am Kontakt (Kontaktwege, Adressen,
// Kennungen) nach v4: Brotkrume "Kontakte / <Name> / <Bereich>", eine h1 mit
// dem Seitenzweck, darunter die Liste ZUERST. Das Formular zum Anlegen
// erscheint erst auf Anforderung (5.2 progressive Offenlegung; CM-UX-004).
//
// GENAU EINE HAUPTAKTION: zu ist der Oeffner "<X> hinzufügen" primary; offen
// ist er ausgeblendet, und das Absenden im Formular ist die Hauptaktion. Ist
// die Liste leer, steht das Formular gleich offen -- dann gibt es nichts
// anderes zu tun.
//
// FOKUS (WCAG 2.4.3): Oeffnen setzt ihn ins erste Feld, Abbrechen und Escape
// zurueck auf den Oeffner. Nach dem Absenden fuehrt der Pflegeabschnitt (die
// Statusregion bzw. das erste markierte Feld); deshalb schliesst das Formular
// NUR nach Erfolg, nie nach einer Ablehnung -- die Eingabe bleibt stehen.

import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react';
import { Button, makeStyles, Title3 } from '@fluentui/react-components';

import { partyHref } from '../parties.ts';
import { Brotkrume } from '../shell/brotkrume.tsx';
import { useSeitenueberschrift, useUeberschriftImInhalt } from '../shell/layout.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

const useStyles = makeStyles({
  kopf: { display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '1rem' },
  titel: { margin: 0, overflowWrap: 'anywhere' },
  aktionen: { display: 'flex', flexWrap: 'wrap', gap: '0.75rem' },
});

/**
 * Brotkrume und h1 einer Pflegeseite. Ohne Namen (laden, unbekannt, oder die
 * Antwort traegt keinen wie bei den Adressen) heisst der Schritt "Kontakt" --
 * der Rueckweg bleibt, die h1 nennt nur den Bereich.
 */
export function PflegeKopf({ partyId, name, bereich }: { readonly partyId: string; readonly name: string | undefined; readonly bereich: string }): ReactElement {
  useUeberschriftImInhalt();
  const titel = name === undefined ? bereich : `${bereich} von ${name}`;
  useSeitenueberschrift(titel);
  const own = useStyles();
  return (
    <div className={own.kopf}>
      <Brotkrume schritte={[
          { label: 'Kontakte', to: '/kontakte' },
          { label: name ?? 'Kontakt', to: partyHref(partyId) },
        ]} hier={bereich} />
      <Title3 as="h1" className={own.titel}>
        {titel}
      </Title3>
    </div>
  );
}

export interface AnlegenSteuerung {
  readonly offen: boolean;
  readonly oeffnen: () => void;
  readonly schliessen: () => void;
  /** Nach Erfolg: zu, ohne den Fokus anzufassen (die Statusregion hat ihn). */
  readonly erledigt: () => void;
  readonly oeffnerRef: RefObject<HTMLButtonElement | null>;
  readonly formRef: RefObject<HTMLFormElement | null>;
}

/**
 * Der Zustand des aufklappenden Formulars. `listeLeer` oeffnet es, solange
 * niemand es ausdruecklich geschlossen oder geoeffnet hat. `kontakt` bindet die
 * Wahl an den Kontakt: wechselt die Seite ohne Neuladen zu einem anderen, gilt
 * sie nicht weiter (der Bildschirm wird ohne Schluessel wiederverwendet).
 */
export function useAnlegen(listeLeerGemeldet: boolean, kontakt: string, laedt = false): AnlegenSteuerung {
  const [wahl, setWahl] = useState<{ readonly kontakt: string; readonly offen: boolean } | undefined>(undefined);
  const [fokus, setFokus] = useState<'feld' | 'oeffner' | undefined>(undefined);
  const oeffnerRef = useRef<HTMLButtonElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  // Wechselt die Seite ohne Neuladen den Kontakt, liefert der Abschnitt einen Render lang
  // noch die Daten des vorigen mit "geladen" und laedt erst danach. "Liste leer" gilt
  // deshalb erst, wenn fuer DIESEN Kontakt ein Laden gesehen wurde und es fertig ist --
  // sonst oeffnete die leere Liste des vorigen Kontakts das Formular am naechsten.
  const [geladenFuer, setGeladenFuer] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (laedt) setGeladenFuer(kontakt);
  }, [laedt, kontakt]);
  const listeLeer = listeLeerGemeldet && !laedt && geladenFuer === kontakt;
  const gewaehlt = wahl?.kontakt === kontakt ? wahl.offen : undefined;
  const offen = gewaehlt ?? listeLeer;
  // Einmal wegen leerer Liste offen, bleibt es offen: laedt der Abschnitt nach einem
  // abgebrochenen Absenden still nach und findet die neue Zeile, darf das Formular
  // mit der stehenden Eingabe nicht zuklappen.
  useEffect(() => {
    if (listeLeer && gewaehlt === undefined) setWahl({ kontakt, offen: true });
  }, [listeLeer, gewaehlt, kontakt]);

  // Nach dem Umschalten per Effekt: das Ziel haengt erst nach dem Rendern im Baum.
  useEffect(() => {
    if (fokus === 'feld' && offen) {
      formRef.current?.querySelector<HTMLElement>('input:not([type="hidden"]):checked, input:not([type="radio"]):not([type="hidden"]), textarea, select')?.focus();
      setFokus(undefined);
    } else if (fokus === 'oeffner' && !offen) {
      oeffnerRef.current?.focus();
      setFokus(undefined);
    }
  }, [fokus, offen]);

  const oeffnen = useCallback(() => {
    setWahl({ kontakt, offen: true });
    setFokus('feld');
  }, [kontakt]);
  const schliessen = useCallback(() => {
    setWahl({ kontakt, offen: false });
    setFokus('oeffner');
  }, [kontakt]);
  const erledigt = useCallback(() => setWahl({ kontakt, offen: false }), [kontakt]);
  return { offen, oeffnen, schliessen, erledigt, oeffnerRef, formRef };
}

/**
 * Der Oeffner, solange das Formular zu ist; die Hauptaktion der Seite. Steht
 * eine andere Hauptaktion (Erneut versuchen, eine offene Rueckfrage), tritt er
 * zurueck (`zuruecktreten`), damit es genau eine bleibt (NFR-UX-004).
 */
export function AnlegenOeffner({
  steuerung,
  label,
  disabled,
  zuruecktreten,
}: {
  readonly steuerung: AnlegenSteuerung;
  readonly label: string;
  readonly disabled: boolean;
  readonly zuruecktreten: boolean;
}): ReactElement | null {
  if (steuerung.offen) return null;
  return (
    <Button
      // Ein Rueckruf statt des Ref-Objekts: Fluent typisiert `ref` unter React 19 als Ref<never>.
      ref={(knoten: HTMLButtonElement | null) => {
        steuerung.oeffnerRef.current = knoten;
      }}
      appearance={zuruecktreten ? 'secondary' : 'primary'}
      data-anlegen-oeffner=""
      // Fokussierbar gesperrt: haelt er den Fokus, waehrend ein Befehl laeuft, bleibt der dort (WCAG 2.4.3).
      disabledFocusable={disabled}
      onClick={steuerung.oeffnen}
    >
      {label}
    </Button>
  );
}

/**
 * Das Formular mit "Abbrechen" neben dem Absenden. Escape schliesst wie
 * Abbrechen -- ausser eine offene Auswahlliste faengt ihn vorher ab.
 */
export function AnlegenFormular({
  steuerung,
  name,
  className,
  onSenden,
  absenden,
  children,
}: {
  readonly steuerung: AnlegenSteuerung;
  /** Der Name des Formulars, wie der Oeffner: er ist weg, sobald es offen ist. */
  readonly name: string;
  readonly className: string;
  readonly onSenden: () => void;
  /** Der Absendeknopf; der Bildschirm bestimmt Text, Betonung und Sperre. */
  readonly absenden: ReactNode;
  readonly children: ReactNode;
}): ReactElement | null {
  const own = useStyles();
  if (!steuerung.offen) return null;
  return (
    <form
      ref={steuerung.formRef}
      data-anlegen-formular=""
      aria-label={name}
      className={className}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSenden();
      }}
      onKeyDown={(event) => {
        // Eine offene Auswahlliste (aria-expanded am Ausloeser) schliesst zuerst nur sich selbst.
        const ausloeser = event.target instanceof HTMLElement ? event.target.closest('[aria-expanded="true"]') : null;
        if (event.key === 'Escape' && !event.defaultPrevented && ausloeser === null) {
          event.preventDefault();
          steuerung.schliessen();
        }
      }}
    >
      {children}
      <div className={own.aktionen}>
        {absenden}
        <NebenKnopf type="button" onClick={steuerung.schliessen}>
          Abbrechen
        </NebenKnopf>
      </div>
    </form>
  );
}
